import React, { useState } from 'react';
import { SubscriptionTier } from '../services/membershipService';

interface DowngradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (tierLevel: number) => Promise<void>;
  currentTier: SubscriptionTier;
  targetTier: SubscriptionTier;
  currentPeriodEnd?: number;
  isLoading?: boolean;
}

const DowngradeConfirmationModal: React.FC<DowngradeConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentTier,
  targetTier,
  currentPeriodEnd,
  isLoading = false,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(targetTier.tier_level);
    } finally {
      setIsConfirming(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isDowngradeToFree = targetTier.tier_level === 1;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-lg shadow-xl max-w-md w-full mx-4'>
        <div className='p-6'>
          <div className='flex items-center mb-4'>
            <div className='w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mr-3'>
              <svg
                className='w-6 h-6 text-orange-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>
            <h3 className='text-lg font-semibold text-gray-900'>
              Confirm {isDowngradeToFree ? 'Cancellation' : 'Downgrade'}
            </h3>
          </div>

          <div className='mb-6'>
            <p className='text-gray-700 mb-4'>
              {isDowngradeToFree ? (
                <>
                  You're about to cancel your subscription and move to the free
                  tier. Your subscription will remain active until the end of
                  your billing period.
                </>
              ) : (
                <>
                  You're about to downgrade your subscription from{' '}
                  <strong>{currentTier.name}</strong> to{' '}
                  <strong>{targetTier.name}</strong>. This change will take
                  effect at your next billing cycle.
                </>
              )}
            </p>

            {/* Additional Minutes Billing Notice */}
            <div className='bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4'>
              <div className='flex items-start'>
                <svg className='w-5 h-5 text-amber-600 mt-0.5 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
                <div className='text-sm'>
                  <p className='font-medium text-amber-900'>Billing Notice</p>
                  <p className='text-amber-700'>
                    Any additional minutes used beyond your plan allowance will still appear on your next bill payment, 
                    regardless of this downgrade.
                  </p>
                </div>
              </div>
            </div>

            {/* Comparison */}
            <div className='bg-gray-50 rounded-lg p-4 mb-4'>
              <div className='flex justify-between items-center mb-2'>
                <span className='font-medium text-gray-900'>Current Plan</span>
                <div className='text-right'>
                  <div className='font-semibold text-gray-900'>
                    {currentTier.name}
                  </div>
                  <div className='text-sm text-gray-600'>
                    {currentTier.monthly_minutes} minutes/month
                  </div>
                </div>
              </div>
              <div className='border-t border-gray-200 pt-2'>
                <div className='flex justify-between items-center'>
                  <span className='font-medium text-gray-900'>New Plan</span>
                  <div className='text-right'>
                    <div className='font-semibold text-gray-900'>
                      {targetTier.name}
                    </div>
                    <div className='text-sm text-gray-600'>
                      {targetTier.monthly_minutes === 0
                        ? 'Limited'
                        : targetTier.monthly_minutes}{' '}
                      minutes/month
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Effective Date */}
            {currentPeriodEnd && (
              <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                <div className='flex items-start'>
                  <svg
                    className='w-5 h-5 text-blue-600 mt-0.5 mr-2'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  <div className='text-sm'>
                    <p className='font-medium text-blue-900'>Effective Date</p>
                    <p className='text-blue-700'>
                      {isDowngradeToFree
                        ? 'Subscription ends'
                        : 'Downgrade takes effect'}
                      : {formatDate(currentPeriodEnd)}
                    </p>
                    <p className='text-blue-700 mt-1'>
                      You'll continue to have access to your current plan until
                      then.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isDowngradeToFree && (
              <div className='bg-red-50 border border-red-200 rounded-lg p-3 mt-3'>
                <div className='flex items-start'>
                  <svg
                    className='w-5 h-5 text-red-600 mt-0.5 mr-2'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  <div className='text-sm'>
                    <p className='font-medium text-red-900'>Important</p>
                    <p className='text-red-700'>
                      Free tier has limited features and usage restrictions. You
                      can resubscribe anytime.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className='flex gap-3'>
            <button
              onClick={onClose}
              disabled={isConfirming}
              className='flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 rounded-lg font-medium transition-colors'
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isConfirming}
              className='flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors'
            >
              {isConfirming ? (
                <div className='flex items-center justify-center'>
                  <svg
                    className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                `Confirm ${isDowngradeToFree ? 'Cancellation' : 'Downgrade'}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DowngradeConfirmationModal;
